import React, { useEffect, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import DiscountFields, { discountToForm, discountToPayload } from "./DiscountFields";
import { useGate } from "./team/Gated";
import AppSelect from "./ui/AppSelect";

/**
 * StoreOfferCard: one offer across a vendor's whole catalogue, or the listed
 * sections only, edited from the vendor profile page of every vertical.
 *
 * `value` is the vendor's stored `storeOffer`; `sections` is the vendor's
 * section list ({ _id, title }); `onSave(storeOffer)` receives the API shape.
 * An item's own discount always wins over this offer; they never stack.
 */
export default function StoreOfferCard({ value, sections = [], onSave, saving = false, currency = "UGX" }) {
  // Vendor team permissions: a store-wide offer is a price change (pricing:edit).
  const pricingGate = useGate("pricing:edit");
  const [form, setForm] = useState(() => ({
    ...discountToForm(value),
    appliesTo: value?.appliesTo === "SECTIONS" ? "SECTIONS" : "ALL",
    sectionIds: Array.isArray(value?.sectionIds) ? value.sectionIds.map(String) : [],
  }));

  useEffect(() => {
    setForm({
      ...discountToForm(value),
      appliesTo: value?.appliesTo === "SECTIONS" ? "SECTIONS" : "ALL",
      sectionIds: Array.isArray(value?.sectionIds) ? value.sectionIds.map(String) : [],
    });
  }, [value]);

  const needsSections = form.enabled && form.appliesTo === "SECTIONS" && form.sectionIds.length === 0;

  const submit = (e) => {
    e.preventDefault();
    if (needsSections) return;
    onSave({
      ...discountToPayload(form),
      appliesTo: form.appliesTo,
      sectionIds: form.appliesTo === "SECTIONS" ? form.sectionIds : [],
    });
  };

  const endNow = () => {
    onSave({ ...discountToPayload({ ...form, enabled: false }), appliesTo: form.appliesTo, sectionIds: [] });
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title as="h4">Store-wide offer</Card.Title>
        <p className="card-category">A deal tag on your card and every item, without editing each one</p>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={submit}>
          <DiscountFields
            value={form}
            onChange={(next) => setForm((prev) => ({ ...prev, ...next }))}
            currency={currency}
            idPrefix="store-offer"
            switchLabel="Run an offer on the whole catalogue"
            helpText="Shows a deal tag on your store card and on every item it covers, with the old price struck through. An item's own discount wins over this offer; the two are never added together."
            showPreview={false}
          />

          {form.enabled ? (
            <Form.Group className="mb-3">
              <Form.Label>Applies to</Form.Label>
              <div className="d-flex" style={{ gap: 16 }}>
                <Form.Check
                  type="radio"
                  id="store-offer-all"
                  name="store-offer-scope"
                  label="Whole catalogue"
                  checked={form.appliesTo === "ALL"}
                  onChange={() => setForm((prev) => ({ ...prev, appliesTo: "ALL" }))}
                />
                <Form.Check
                  type="radio"
                  id="store-offer-sections"
                  name="store-offer-scope"
                  label="Selected sections"
                  checked={form.appliesTo === "SECTIONS"}
                  onChange={() => setForm((prev) => ({ ...prev, appliesTo: "SECTIONS" }))}
                />
              </div>
              {form.appliesTo === "SECTIONS" ? (
                <>
                  <AppSelect block
                    multiple
                    className="mt-2"
                    value={form.sectionIds}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        sectionIds: Array.from(e.target.selectedOptions).map((o) => o.value),
                      }))
                    }
                    isInvalid={needsSections}
                    style={{ minHeight: 120 }}
                  >
                    {sections.map((section) => (
                      <option key={section._id} value={String(section._id)}>
                        {section.title || section.name || "Untitled section"}
                      </option>
                    ))}
                  </AppSelect>
                  <Form.Control.Feedback type="invalid">Pick at least one section.</Form.Control.Feedback>
                  <Form.Text className="text-muted">Hold Ctrl or Cmd to pick more than one.</Form.Text>
                </>
              ) : null}
            </Form.Group>
          ) : null}

          <div className="d-flex" style={{ gap: 10 }}>
            <Button type="submit" variant="primary" disabled={saving || needsSections || !pricingGate.allowed} title={pricingGate.title}>
              {saving ? "Saving..." : "Save offer"}
            </Button>
            {value?.enabled ? (
              <Button type="button" variant="outline-secondary" disabled={saving || !pricingGate.allowed} title={pricingGate.title} onClick={endNow}>
                End offer now
              </Button>
            ) : null}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
